


























































import OfferEvent from '@core/models/offer/OfferEvent';
import { SportOfferDataStore } from '@store/modules/data/sportOffer';
import {
    useVueRouter,
    useVuexStore,
} from '@superbet-group/web.lib.vue-utilities';
import { SelectionSourceScreen } from '@superbet-group/web.types';
import {
    computed,
    defineComponent,
    onUnmounted,
    PropType,
    ref,
    watch,
} from '@vue/composition-api';
import EventRow from '@modules/eventGrid/components/EventRow/index.vue';
import { SdsSectionTitle } from '@superbet-group/web.lib.ui-components';
import { getPrimaryMarket } from '@store/modules/ui/sportOffer/marketFilters/rawFunctions';
import { BetslipStore } from '@store/modules/ui/betSlip';
import EventRowSkeleton from '@modules/eventGrid/components/EventRow/EventRowSkeleton.vue';
import { RouteName } from '@src/app/router/constants/RouteName';
import {
    EventCardSource,
    ExpandedEventInUrl,
} from '@src/app/enums';
import { getUniqueTournamentId } from '@store/modules/ui/sportOffer/marketFilters/helpers';
import { getEventSlugFromEvent } from '@modules/eventDetailsPage/utils';
import InPlaySportSwitcher from '@modules/home/InPlaySportSwitcher.vue';
import { SportOfferMarketsStore } from '@store/modules/ui/sportOffer/markets';
import { ALL_ID } from '@store/modules/ui/sportOffer/constants';
import { Link } from '@src/app/router/constants/Link';
import { TimeFilterSlug } from '@store/modules/ui/shared/timeFilter/utils';
import {
    getParamWithPrefix,
    ParamTypes,
    Prefix,
    SectionVisibilityAnalyticNames,
} from '@src/app/utils/analyticsScreenNames';
import { useSendSectionViewportVisibilityAnalyticsEvent } from '@shared/composables/useSendSectionViewportVisibilityAnalyticsEvent';
import { Analytics as AnalyticsSDK } from '@superbet-group/web.analytics.sdk';
import { ClickName } from '@superbet-group/product.analytics';
import {
    createSelfCleaningClientStorage,
    SelfCleaningClientStorage,
} from '@core/utils/selfCleaningClientStorage/createSelfCleaningClientStorage';
import { PeerUrgencyExperimentVariant } from '@core/models/user/flags';

export default defineComponent({
    props: {
        items: {
            type: Array as PropType<OfferEvent[]>,
            required: true,
        },
        totalItemsCount: {
            type: Number,
            required: false,
            default: undefined,
        },
        title: {
            type: String,
            required: true,
        },
        areEventsFetched: {
            type: Boolean,
            required: true,
        },
        sectionPath: {
            type: String,
            default: null,
        },
        sourceScreen: {
            type: Number as PropType<SelectionSourceScreen>,
            required: false,
            default: SelectionSourceScreen.Unknown,
        },
        eventCardSource: {
            type: String as PropType<EventCardSource>,
            required: true,
        },
        bettorNumberCache: {
            type: Object as PropType<SelfCleaningClientStorage<number, number>>,
            required: false,
            default: () => createSelfCleaningClientStorage<number, number>('bettorNumberCache'),
        },
        socialProofDesignVariant: {
            type: String as PropType<PeerUrgencyExperimentVariant>,
            required: false,
            default: PeerUrgencyExperimentVariant.None,
        },
        titleDescription: {
            type: String,
            required: false,
            default: undefined,
        },
    },
    setup(props) {
        const sportOfferDataStore = useVuexStore<SportOfferDataStore>('data/sportOffer');
        const sportOfferBetslipStore = useVuexStore<BetslipStore>('ui/sportOffer/betSlip');
        const marketsStore = useVuexStore<SportOfferMarketsStore>('ui/sportOffer/markets');
        const nonFinalMarketsConfig = marketsStore.state('nonFinalMarketsConfig');
        const fetchNonFinalMarketsConfig = marketsStore.action('fetchNonFinalMarketsConfig');
        const getEvents = sportOfferDataStore.action('getEvents');
        const unsubscribeFromEvent = sportOfferDataStore.action('removeSubscription');
        const selectedSportSlug = ref('');
        const selectedSportId = ref(ALL_ID);

        if (!nonFinalMarketsConfig.value) {
            (async function () {
                await fetchNonFinalMarketsConfig();
            })();
        }

        const { observerRef } = useSendSectionViewportVisibilityAnalyticsEvent(SectionVisibilityAnalyticNames.HomeInPlay);

        const liveEventsForSelectedSport = computed(() => {
            return selectedSportId.value === ALL_ID ? props.items : props.items.filter((event) => event.sportId === selectedSportId.value);
        });

        const displayedItemsCount = computed(() => selectedSportId.value === ALL_ID ? props.totalItemsCount : liveEventsForSelectedSport.value.length);

        const setSelectedSport = (id: number, slug: string) => {
            selectedSportId.value = id;
            selectedSportSlug.value = slug;
        };

        const previewedEvents = computed(() => liveEventsForSelectedSport.value.slice(0, 4));

        const idsOfPreviewedEvents = computed(() => previewedEvents.value.map((event) => event.id));

        watch(() => idsOfPreviewedEvents.value, (newIds, oldIds) => {
            const filteredNewIds = newIds.filter((id) => !oldIds?.includes(id));
            const filteredOldIds = oldIds?.filter((id) => !newIds.includes(id));

            getEvents(filteredNewIds);

            filteredOldIds?.forEach((id) => {
                unsubscribeFromEvent(id);
            });
        }, { immediate: true });

        onUnmounted(() => {
            idsOfPreviewedEvents.value.forEach((id) => {
                unsubscribeFromEvent(id);
            });
        });

        const onEventClick = (event: OfferEvent) => {
            const eventIndex = previewedEvents.value.indexOf(event);
            AnalyticsSDK.getInstance().log('click', {
                click_name: ClickName.MATCH_DETAILS_CLICK,
                click: {
                    click_value: eventIndex.toString(),
                    type: 'general_click',
                },
            });

            router.value.push({
                name: RouteName.Sport.OfferEvent,
                params: { eventSlug: getEventSlugFromEvent(event) },
                query: {
                    [ExpandedEventInUrl.type]: getUniqueTournamentId(
                        'offer',
                        event.tournamentId,
                        event.isOngoing(),
                    ),
                },
            });
        };

        const { router } = useVueRouter();
        const seeMoreRoute = computed(() => {
            return {
                path: selectedSportId.value === ALL_ID
                    ? [Link.Sport.PrematchOffer, TimeFilterSlug.Live].join('/')
                    : [Link.Sport.PrematchOffer, selectedSportSlug.value, TimeFilterSlug.Live].join('/'),
            };
        });

        const handleSeeMoreClick = () => {
            AnalyticsSDK.getInstance().log('click', {
                click_name: ClickName.HOMEPAGE_SPORT_SWITCHER_LIVE_EVENTS_CLICK,
                click: {
                    sport_code: selectedSportId.value === ALL_ID ? 'all' : getParamWithPrefix(Prefix.AXILIS, ParamTypes.SPORT, selectedSportId.value)!,
                    type: 'sports_click',
                },
            });
        };

        return {
            eventOddMap: sportOfferBetslipStore.getter('eventOddMap'),
            oddValueDynamics: sportOfferDataStore.getter('oddValueDynamics'),
            seeMoreRoute,
            previewedEvents,
            displayedItemsCount,
            selectedSportId,
            observerRef,

            setSelectedSport,
            getPrimaryMarket,
            onEventClick,
            handleSeeMoreClick,
        };
    },
    components: {
        EventRow,
        SdsSectionTitle,
        EventRowSkeleton,
        InPlaySportSwitcher,
    },
});
